import { useEffect, useState } from 'react'
import { Button } from 'antd'
import useComments from '../../hooks/useComments'
import CommentItem from './CommentItem'
import ReplyList from './ReplyList'
import CommentInput from './CommentInput'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import endpoints from '../../services/endpoints'
import { useRef } from 'react'
import { useEmbeddedAuth } from '../../contexts/EmbeddedAuthContext'
import { getCurrentUser } from '../../services/authService'

/**
 * 评论区域入口组件
 * @param {{ contentId: string|number }} props - 关联内容ID
 */
export default function Comments({ contentId }) {
  const { comments, addCommentTop, addReplyTop, expandReplies, isExpanded, toggleLike, createComment, pagination, loadComments, loading } = useComments(contentId)
  const { showLoginModal } = useEmbeddedAuth()
  const [showInput, setShowInput] = useState(false)
  const [replyTo, setReplyTo] = useState(null)
  const totalCount = pagination?.total || 0
  const [interactionCount, setInteractionCount] = useState(null)
  const navigate = useNavigate()
  const inlineInputRef = useRef()

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const res = await api.get(endpoints.explorations.detail(contentId))
        if (mounted) setInteractionCount(res?.interactions?.comment_count ?? null)
      } catch {}
    })()
    return () => { mounted = false }
  }, [contentId])

  /**
   * 发送评论或回复
   */
  const handleSubmit = async ({ content, mentions }) => {
    try {
      if (replyTo) {
        const payload = { content, reply_to_id: replyTo.commentId, ...(replyTo.userId ? { reply_to_user_id: replyTo.userId } : {}), ...(mentions?.length ? { mentions } : {}) }
        
        const res = await createComment(contentId, payload)
        addReplyTop(replyTo.parentId, res, replyTo.userId, replyTo.userName)
      } else {
        const res = await createComment(contentId, { content, mentions })
        addCommentTop(res)
      }
      setInteractionCount((prev) => (prev == null ? (pagination?.total || 0) + 1 : prev + 1))
      setShowInput(false)
      setReplyTo(null)
    } catch {}
  }

  /**
   * 触发回复：定位父评论并记录被回复用户
   */
  const handleReply = (commentOrReply) => {
    if (!getCurrentUser()) {
      showLoginModal()
      return
    }
    const isChild = !!(commentOrReply?.fields?.reply_to_id || commentOrReply?.reply_to_id)
    const parentId = isChild ? (commentOrReply?.fields?.reply_to_id || commentOrReply?.reply_to_id) : commentOrReply?.id
    const commentId = commentOrReply?.id
    const userId = commentOrReply?.user?.id
    const userName = commentOrReply?.user?.name
    setReplyTo({ parentId, commentId, userId, userName })
    setShowInput(true)
  }

  const handleLike = (id) => {
    if (!getCurrentUser()) {
      showLoginModal()
      return
    }
    toggleLike(id)
  }

  /**
   * 点击 @用户名 跳转到个人详情
   */
  const handleMentionClick = (userId) => {
    if (!userId) return
    navigate(`/personal-info/${userId}`)
  }

  const estimatedTotal = (pagination?.total || 0) + (comments || []).reduce((acc, c) => acc + (c.replies?.total_count || 0), 0)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 8 }}>
        <div>评论（{interactionCount ?? estimatedTotal}）</div>
      </div>
      <div style={{ padding: 8, border: '1px solid var(--color-border-secondary)', borderRadius: 8, marginBottom: 12 }}>
        <CommentInput inline inputRef={inlineInputRef} onSubmit={handleSubmit} placeholder="写下你的评论..." />
      </div>
      {comments.map((c) => (
        <div key={c.id}>
          <CommentItem comment={c} onReply={handleReply} onLike={handleLike} onMentionClick={handleMentionClick} />
          <ReplyList parentComment={c} replies={c.replies?.list || []} onReply={handleReply} onLike={handleLike} onExpand={(id) => expandReplies(id)} isExpanded={isExpanded(c.id)} onMentionClick={handleMentionClick} />
      </div>
      ))}
      <div style={{ padding: 8 }}>
        {pagination?.page < (pagination?.total_pages || 1) && (
          <Button type="link" loading={loading} onClick={() => loadComments((pagination.page || 1) + 1, pagination.per_page || 20)}>
            加载更多
          </Button>
        )}
      </div>
      <CommentInput open={showInput} onClose={() => setShowInput(false)} onSubmit={handleSubmit} defaultReplyUser={replyTo?.userName} />
    </div>
  )
}
